import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
  Card,
  CardContent,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  GetApp as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

// Assuming we'll create these actions in a membersSlice
import { getMembers, exportMembers } from '../../features/members/membersSlice';
import MemberDetailDialog from '../../components/admin/MemberDetailDialog';

const MembersList = () => {
  const dispatch = useDispatch();
  const { members, totalMembers, pagination, loading, exporting, error } = useSelector((state) => state.members);
  
  // Local state for pagination and filtering
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Separate state for filter form (what user is editing)
  const [filters, setFilters] = useState({
    status: 'all', // 'all', 'active', 'inactive'
    sortBy: 'newest', // 'newest', 'oldest', 'name_asc', 'name_desc'
    ticketsRange: [0, 1000], // [min, max] tickets count
    giveawaysRange: [0, 100], // [min, max] giveaways won count
  });
  
  // Applied filters state (what's actually used in API calls)
  const [appliedFilters, setAppliedFilters] = useState({
    status: 'all',
    sortBy: 'newest',
    ticketsRange: [0, 1000],
    giveawaysRange: [0, 100],
  });
  
  // Dialog state
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    member: null,
  });

  // Separate state for the actual search query used in API calls
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Fetch members with pagination and applied filters
    dispatch(
      getMembers({
        page: page + 1, // API uses 1-indexed pages
        perPage: rowsPerPage,
        search: searchQuery,
        status: appliedFilters.status !== 'all' ? appliedFilters.status : '',
        minTickets: appliedFilters.ticketsRange[0],
        maxTickets: appliedFilters.ticketsRange[1],
        minGiveaways: appliedFilters.giveawaysRange[0],
        maxGiveaways: appliedFilters.giveawaysRange[1],
      })
    );
  }, [dispatch, page, rowsPerPage, searchQuery, appliedFilters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Update search query to trigger API call
    setSearchQuery(searchTerm);
    // Reset to first page when searching
    setPage(0);
  };

  const handleExport = () => {
    dispatch(exportMembers({
      search: searchQuery,
      status: appliedFilters.status !== 'all' ? appliedFilters.status : undefined,
      sortBy: appliedFilters.sortBy,
      minTickets: appliedFilters.ticketsRange[0],
      maxTickets: appliedFilters.ticketsRange[1],
      minGiveaways: appliedFilters.giveawaysRange[0],
      maxGiveaways: appliedFilters.giveawaysRange[1],
    }));
  };

  const handleRefresh = () => {
    dispatch(
      getMembers({
        page: page + 1,
        perPage: rowsPerPage,
        search: searchQuery,
        status: appliedFilters.status !== 'all' ? appliedFilters.status : '',
        sortBy: appliedFilters.sortBy,
        minTickets: appliedFilters.ticketsRange[0],
        maxTickets: appliedFilters.ticketsRange[1],
        minGiveaways: appliedFilters.giveawaysRange[0],
        maxGiveaways: appliedFilters.giveawaysRange[1],
      })
    );
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(0); // Reset to first page when filters are applied
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      status: 'all',
      sortBy: 'newest',
      ticketsRange: [0, 1000],
      giveawaysRange: [0, 100],
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(0);
  };

  const hasActiveFilters = () => {
    return (
      appliedFilters.status !== 'all' ||
      appliedFilters.sortBy !== 'newest' ||
      appliedFilters.ticketsRange[0] !== 0 ||
      appliedFilters.ticketsRange[1] !== 1000 ||
      appliedFilters.giveawaysRange[0] !== 0 ||
      appliedFilters.giveawaysRange[1] !== 100
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom>
        Members
      </Typography>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mr: 2, width: 300 }}
            />
            <Button
              variant="contained"
              type="submit"
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Box>

          <Box>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ mr: 1 }}
            >
              Filters{" "}
              {hasActiveFilters() &&
                `(${
                  Object.values(filters).filter(
                    (v) =>
                      v !== "all" &&
                      v !== "newest" &&
                      (Array.isArray(v)
                        ? v[0] !== 0 ||
                          v[1] !== (v === filters.ticketsRange ? 1000 : 100)
                        : false)
                  ).length
                })`}
            </Button>
            {hasActiveFilters() && (
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{ mr: 1 }}
              >
                Clear Filters
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={
                exporting ? <CircularProgress size={20} /> : <DownloadIcon />
              }
              onClick={handleExport}
              disabled={loading || exporting}
              sx={{
                color: (theme) =>
                  loading || exporting ? theme.palette.common.white : undefined,
              }}
            >
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          </Box>
        </Box>

        {/* Advanced Filters Panel */}
        <Collapse in={showFilters}>
          <Divider sx={{ mb: 3 }} />
          <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Advanced Filters
              </Typography>

              <Grid container spacing={3} alignItems="flex-start">
                {/* Status Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sort By Filter */}
                {/* <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={filters.sortBy}
                      label="Sort By"
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    >
                      <MenuItem value="newest">Newest First</MenuItem>
                      <MenuItem value="oldest">Oldest First</MenuItem>
                      <MenuItem value="name_asc">Name A-Z</MenuItem>
                      <MenuItem value="name_desc">Name Z-A</MenuItem>
                      <MenuItem value="tickets_desc">Most Tickets</MenuItem>
                      <MenuItem value="tickets_asc">Least Tickets</MenuItem>
                      <MenuItem value="giveaways_desc">Most Giveaways Won</MenuItem>
                      <MenuItem value="giveaways_asc">Least Giveaways Won</MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}

                {/* Tickets Range Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        mb: 1.5,
                        color: "text.primary",
                      }}
                    >
                      Tickets Count Range
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        mt: "auto",
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Min"
                        value={filters.ticketsRange[0]}
                        onChange={(e) => {
                          const newMin = Math.max(
                            0,
                            parseInt(e.target.value) || 0
                          );
                          handleFilterChange("ticketsRange", [
                            newMin,
                            filters.ticketsRange[1],
                          ]);
                        }}
                        inputProps={{ min: 0, max: filters.ticketsRange[1] }}
                        sx={{ width: "85px" }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mx: 0.5 }}
                      >
                        to
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        label="Max"
                        value={filters.ticketsRange[1]}
                        onChange={(e) => {
                          const newMax = Math.max(
                            filters.ticketsRange[0],
                            parseInt(e.target.value) || 0
                          );
                          handleFilterChange("ticketsRange", [
                            filters.ticketsRange[0],
                            newMax,
                          ]);
                        }}
                        inputProps={{ min: filters.ticketsRange[0] }}
                        sx={{ width: "85px" }}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Giveaways Won Range Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        mb: 1.5,
                        color: "text.primary",
                      }}
                    >
                      Giveaways Won Range
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        mt: "auto",
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Min"
                        value={filters.giveawaysRange[0]}
                        onChange={(e) => {
                          const newMin = Math.max(
                            0,
                            parseInt(e.target.value) || 0
                          );
                          handleFilterChange("giveawaysRange", [
                            newMin,
                            filters.giveawaysRange[1],
                          ]);
                        }}
                        inputProps={{ min: 0, max: filters.giveawaysRange[1] }}
                        sx={{ width: "85px" }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mx: 0.5 }}
                      >
                        to
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        label="Max"
                        value={filters.giveawaysRange[1]}
                        onChange={(e) => {
                          const newMax = Math.max(
                            filters.giveawaysRange[0],
                            parseInt(e.target.value) || 0
                          );
                          handleFilterChange("giveawaysRange", [
                            filters.giveawaysRange[0],
                            newMax,
                          ]);
                        }}
                        inputProps={{ min: filters.giveawaysRange[0] }}
                        sx={{ width: "85px" }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Filter Actions */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {hasActiveFilters()
                    ? "Filters will be applied when you click Apply"
                    : "No filters applied"}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters()}
                    size="small"
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    size="small"
                  >
                    Apply Filters
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Collapse>
      </Paper>

      {/* Active Filters Summary */}
      {hasActiveFilters() && !showFilters && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "primary.50",
            border: "1px solid",
            borderColor: "primary.200",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
              Active Filters:
            </Typography>
            {appliedFilters.status !== "all" && (
              <Chip
                label={`Status: ${appliedFilters.status}`}
                size="small"
                onDelete={() => {
                  handleFilterChange("status", "all");
                  setAppliedFilters((prev) => ({ ...prev, status: "all" }));
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {appliedFilters.sortBy !== "newest" && (
              <Chip
                label={`Sort: ${appliedFilters.sortBy.replace("_", " ")}`}
                size="small"
                onDelete={() => {
                  handleFilterChange("sortBy", "newest");
                  setAppliedFilters((prev) => ({ ...prev, sortBy: "newest" }));
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {(appliedFilters.ticketsRange[0] !== 0 ||
              appliedFilters.ticketsRange[1] !== 1000) && (
              <Chip
                label={`Tickets: ${appliedFilters.ticketsRange[0]}-${appliedFilters.ticketsRange[1]}`}
                size="small"
                onDelete={() => {
                  handleFilterChange("ticketsRange", [0, 1000]);
                  setAppliedFilters((prev) => ({
                    ...prev,
                    ticketsRange: [0, 1000],
                  }));
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {(appliedFilters.giveawaysRange[0] !== 0 ||
              appliedFilters.giveawaysRange[1] !== 100) && (
              <Chip
                label={`Giveaways: ${appliedFilters.giveawaysRange[0]}-${appliedFilters.giveawaysRange[1]}`}
                size="small"
                onDelete={() => {
                  handleFilterChange("giveawaysRange", [0, 100]);
                  setAppliedFilters((prev) => ({
                    ...prev,
                    giveawaysRange: [0, 100],
                  }));
                }}
                color="primary"
                variant="outlined"
              />
            )}
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ ml: 1 }}
            >
              Clear All
            </Button>
          </Box>
        </Paper>
      )}

      {/* Members Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tickets</TableCell>
                <TableCell>Giveaways Won</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={40} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : members?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No members found
                  </TableCell>
                </TableRow>
              ) : (
                members?.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member?.id}</TableCell>
                    <TableCell>{`${member?.display_name}`}</TableCell>
                    <TableCell>{member?.user_email}</TableCell>
                    <TableCell>
                      <Chip
                        label={member.status}
                        color={
                          member.status === "active"
                            ? "success"
                            : member.status === "inactive"
                            ? "default"
                            : member.status === "pending"
                            ? "warning"
                            : member.status === "suspended"
                            ? "error"
                            : "default"
                        }
                        size="small"
                        variant={
                          member.status === "active" ? "filled" : "outlined"
                        }
                      />
                    </TableCell>

                    <TableCell>{member.total_entries ?? 0}</TableCell>
                    <TableCell>{member.giveawaysWon}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() => {
                            setDetailDialog({
                              open: true,
                              member: member,
                            });
                          }}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination?.total || totalMembers || members?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      </Paper>

      {/* Member Detail Dialog */}
      <MemberDetailDialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, member: null })}
        member={detailDialog.member}
      />
    </Box>
  );
};

export default MembersList;